import React from 'react';
import styles from '../../styles/UserPage.module.css';
import useModelReliability from '../../hooks/useModelReliability';

/**
 * Shows models currently using simplified (deterministic) agent
 * routing after repeated tool-calling failures, with a per-model
 * "re-enable full agent mode" action.
 *
 * Renders nothing when no model is degraded, so it costs no space in
 * the common case.
 */
export default function ModelReliabilityPanel() {
  const { degradedModels, resetModel } = useModelReliability();

  if (degradedModels.length === 0) return null;

  return (
    <div className={styles.section}>
      <h2 className={styles.sectionTitle}>Agent Mode Status</h2>
      <p className={styles.sectionDescription}>
        These models recently struggled to call agents, so expansive
        and evaluation runs use simplified routing for them. Full
        agent mode is retried automatically about once a day — or
        re-enable it now (e.g. after updating Ollama on your device).
      </p>
      {degradedModels.map((m) => (
        <div
          key={`${m.provider}:${m.model_name}`}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '0.75rem',
            padding: '0.5rem 0.75rem',
            marginBottom: '0.5rem',
            background: 'var(--input-background)',
            border: '1px solid var(--input-border)',
            borderRadius: '6px',
          }}
        >
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
            <span
              title={`${m.consecutive_failures} consecutive agent-call failures`}
              style={{
                padding: '0.1rem 0.45rem',
                borderRadius: '999px',
                fontSize: '0.72rem',
                fontWeight: 600,
                background: 'rgba(245, 158, 11, 0.15)',
                color: '#d97706',
                border: '1px solid rgba(245, 158, 11, 0.4)',
                whiteSpace: 'nowrap',
              }}
            >
              Simplified routing
            </span>
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {m.provider} / {m.model_name}
              </span>
              <span
                style={{ fontSize: '0.72rem', color: 'var(--text-secondary, #9aa0a6)', whiteSpace: 'nowrap' }}
                title="Lifetime agent-run outcomes for this model"
              >
                {m.success_count ?? 0} ok · {m.rescue_count ?? 0} rescued · {m.guard_count ?? 0} failed · {m.degraded_count ?? 0} simplified
              </span>
            </span>
          </span>
          <button
            onClick={() => resetModel(m.provider, m.model_name)}
            className={styles.button}
            style={{ whiteSpace: 'nowrap' }}
            title="Clear failure history and retry full agent mode on the next run"
          >
            Re-enable full agent mode
          </button>
        </div>
      ))}
    </div>
  );
}
